import {
  Invoice,
  InvoicePageQuery,
  InvoiceResource,
  InvoiceResourcePage,
  InvoiceSummary,
  InvoiceSummaryQuery,
  PageOf
} from '@/models'

import axios, { withToken } from './axios'

import {
  generateRead,
  generateReadPage,
  generateUpdateSubresource
} from './crudGenerator'

/**
 * Converts InvoiceResource to Invoice.
 *
 * @param resource Reponse data to be converted.
 */
export function parseInvoiceResource (resource: InvoiceResource): Invoice {
  return {
    id: resource.id,
    businessUnitId: resource.shopId, // Renaming
    code: resource.code,
    name: resource.name,
    phone: resource.mobile, // Renaming
    email: resource.email,
    location: resource.location,
    paymentMethod: resource.paymentMethod,
    createdAt: new Date(resource.createdAt),
    closingAt: resource.closingAt ? new Date(resource.closingAt) : null,
    closedAt: resource.closedAt ? new Date(resource.closedAt) : null,
    status: resource.status,
    change: resource.change,
    deliveryFee: resource.deliveryFee,
    total: resource.total,
    address: resource.address,
    isPrepaid: resource.prepay // Renaming
  }
}

/**
 * Converts InvoiceResourcePage to PageOf<Invoice>.
 *
 * @param resource Reponse data to be converted.
 */
export function parseInvoiceResourcePage (page: InvoiceResourcePage): PageOf<Invoice> {
  return {
    elements: page.invoices.map(parseInvoiceResource),
    total: page.count
  }
}

const url = '/ordering'
const entity = 'invoices'

export default {
  /**
   * Closes an existing Invoice.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param id Invoice id to be closed.
   */
  closeInvoice: generateUpdateSubresource(url, entity, 'closed'),

  /**
   * Gets an Invoice.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param id Invoice id to be fetched.
   */
  fetchInvoice: generateRead(url, entity, parseInvoiceResource),

  /**
   * Gets a page of Invoices.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param query Query options.
   */
  fetchInvoicePage: generateReadPage<Invoice, InvoiceResourcePage, InvoicePageQuery>(url, entity, parseInvoiceResourcePage),

  /**
   * Gets an InvoiceSummary.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param query Query options.
   */
  async fetchInvoiceSummary (tokenV2: string, query: InvoiceSummaryQuery): Promise<InvoiceSummary> {
    const response = await axios.get<InvoiceSummary>(
      `${url}/summary`,
      {
        params: query,
        ...withToken(tokenV2)
      }
    )

    return response.data
  }
}
